import { useLang } from "./LanguageContext";
import * as siteEn from "../data/site";
import * as siteId from "../data/site.id";
import { COMPANIES as companiesEn } from "../data/companies";
import { COMPANIES as companiesId } from "../data/companies.id";

const SITE = {
    en: {
        navLinks: siteEn.NAV_LINKS,
        sectors: siteEn.SECTORS,
        whySadja: siteEn.WHY_SADJA,
        roadmap: siteEn.ROADMAP,
        assetStages: siteEn.ASSET_STAGES,
        leadership: siteEn.LEADERSHIP,
        investorCards: siteEn.INVESTOR_CARDS,
    },
    id: {
        navLinks: siteId.NAV_LINKS,
        sectors: siteId.SECTORS,
        whySadja: siteId.WHY_SADJA,
        roadmap: siteId.ROADMAP,
        assetStages: siteId.ASSET_STAGES,
        leadership: siteId.LEADERSHIP,
        investorCards: siteId.INVESTOR_CARDS,
    },
};

const COMPANIES_BY_LANG = { en: companiesEn, id: companiesId };

export const UI = {
    en: {
        navCta: "Explore Our Group",
        hero: {
            overline: "Sadja Core Corp — Investment Holding Company",
            lines: ["Building the", "Future of", "Energy & Infrastructure"],
            sub: "An investment holding company building scalable platforms across energy, power transmission, and strategic infrastructure.",
            tagline: "Global Vision. Infinite Impact.",
            primary: "Explore Our Group",
            secondary: "Our Companies",
            strip: ["Energy", "Transmission", "Infrastructure"],
            scroll: "Scroll",
            imgAlt: "High voltage electricity transmission towers at sunset",
        },
        about: {
            chapter: "CH.01",
            overline: "About Sadja Core",
            title: "An Investment Holding Company for Next-Gen Platforms",
            lead: "Sadja Core Corp is an investment holding company focused on building scalable platforms across energy, power transmission, and strategic infrastructure sectors.",
            objectiveLabel: "Strategic Objective",
            objective:
                "To build a multi-billion-dollar infrastructure platform serving demand for power generation, grid networks, industrial utilities, and the global energy transition.",
            imgAlt: "Corporate high-rise architecture",
        },
        companies: {
            chapter: "CH.02",
            overline: "Our Group",
            title: "Four Companies. One Platform.",
            description:
                "Sadja Core Corp operates through four focused subsidiaries — each a specialist platform, together an integrated energy and infrastructure group.",
            more: "more capabilities",
            explore: "Explore",
        },
        ecosystem: {
            chapter: "CH.03",
            overline: "Group Ecosystem",
            title: "One Holding. Four Engines.",
            description:
                "Each subsidiary reinforces the others — transmission expertise, power generation capability, and construction delivery combined under one investment platform.",
            centerSub: "INVESTMENT HOLDING",
            branches: [
                { name: "DIGON", sub: "Power Transmission", tag: "GRID", route: "/companies/digon" },
                { name: "ORBITO", sub: "Integrated Energy", tag: "POWER", route: "/companies/orbito" },
                { name: "SADJA GROUP", sub: "Construction, Property & SUTET Stringing", tag: "INFRASTRUCTURE", route: "/companies/sadja-group" },
                { name: "KDS", sub: "General Contractor & Construction", tag: "GENERAL CONTRACTOR", route: "/companies/kharisma-damar-satya" },
            ],
            caption:
                "Digon builds the grid. Orbito powers it. Sadja Group and Kharisma Damar Satya construct the world around it — complementary platforms under a single holding structure.",
        },
        platforms: {
            chapter: "CH.04",
            overline: "Our Platforms",
            title: "Backbone Capabilities of the Group",
            description: "The core execution platforms each operating company brings to the group.",
            tabs: ["PT. Digon Power", "PT. Sadja Group Indonesia", "PT. Orbito Integrated Energy", "PT. Kharisma Damar Satya"],
        },
        why: { chapter: "CH.05", overline: "Why Sadja Core?", title: "Built for the Long Cycle" },
        roadmap: {
            chapter: "CH.06",
            overline: "Strategic Roadmap",
            title: "2026 — 2030",
            description:
                "From operational foundations to a regional infrastructure group — a deliberate, staged evolution from contractor to asset owner.",
            targetMarkets: "Target Markets",
        },
        asset: {
            chapter: "CH.07",
            overline: "PT. Sadja Core Corp.",
            title: "Asset Ownership Strategy",
            description:
                "A three-stage institutional strategy — from asset-light execution to a multi-trillion Rupiah infrastructure ownership platform.",
            stage: "Stage",
        },
        experience: {
            chapter: "CH.08",
            overline: "Projects / Capabilities",
            title: "Group Capabilities & Experience",
            description:
                "Decades of combined delivery across transmission lines, substations, civil construction, and power generation — across Indonesia and Timor-Leste.",
            years: "Years of Experience",
            viewProfile: "View Company Profile",
            allGroup: "All Group",
            openKds: "Open Full Kharisma Damar Satya Profile",
        },
        investors: {
            chapter: "CH.09",
            overline: "Investor Relations",
            titleA: "Building Long-Term",
            titleB: "Infrastructure Value",
            body: "Sadja Core Corp welcomes partnerships with global institutions to transform emerging market infrastructure.",
            cta: "Discuss a Partnership",
        },
        leadership: {
            chapter: "CH.10",
            overline: "Leadership",
            title: "Management Team",
            description:
                "Experienced leadership across corporate strategy, governance, and capital management.",
            openPosition: "Open Position",
        },
        contact: {
            chapter: "CH.11",
            overline: "Contact",
            title: "Start the Conversation",
            description:
                "For partnerships, projects, and investor enquiries — reach the Sadja Core Corp team directly.",
            emailLabel: "Email",
            officeLabel: "Head Office",
            name: "Name",
            email: "Email",
            subject: "Subject",
            message: "Message",
            namePh: "Your full name",
            emailPh: "you@company.com",
            subjectPh: "Partnership / project enquiry",
            messagePh: "Tell us about your project or partnership interest…",
            send: "Send Message",
            toast: "Opening your email client — your message is ready to send.",
            enquiryFrom: "Enquiry from",
            website: "Website",
        },
        kds: {
            seoTitle: "PT. Kharisma Damar Satya | Sadja Core Corp",
            seoDesc: "PT. Kharisma Damar Satya is an experienced Indonesian construction company with project experience across industrial, infrastructure, hospitality, residential, office and commercial sectors.",
            category: "GENERAL CONTRACTOR & CONSTRUCTION",
            headline1: "Building with Experience.",
            headline2: "Delivering with Trust.",
            heroSub: "PT. Kharisma Damar Satya adalah salah satu perusahaan konstruksi swasta di Indonesia yang telah berpengalaman.",
            heroSupport: "We have completed projects across residential, office, commercial, factory, warehouse and infrastructure sectors — headquartered in Jakarta.",
            backTo: "Back to Sadja Core",
            exploreProjects: "Explore Our Projects",
            aboutOverline: "PT. Kharisma Damar Satya",
            aboutTitle: "About Kharisma Damar Satya",
            originalLabel: "Original — Bahasa Indonesia",
            translationLabel: "English Translation",
            vmTitle: "Vision & Mission",
            vision: "Vision",
            mission: "Mission",
            sectorsOverline: "What We Do",
            sectorsTitle: "Our Business Sectors",
            legalityOverline: "Compliance",
            legalityTitle: "Company Legality",
            classOverline: "Registered Scope",
            classTitle: "Business Classifications",
            classSearchPh: "Search code or description…",
            certOverline: "Quality & Safety",
            certTitle: "Certifications & Management Systems",
            portfolioOverline: "Track Record",
            portfolioTitle: "Project Portfolio",
            filterSector: "Sector",
            filterStatus: "Status",
            filterAll: "All",
            fields: { client: "Client", project: "Project", location: "Location", scope: "Scope", sector: "Sector", status: "Status", year: "Year" },
            backToPortfolio: "Back to Project Portfolio",
            expOverline: "Capabilities",
            expTitle: "Our Experience",
            contactOverline: "Get in Touch",
            contactTitle: "Contact",
            addressLabel: "Office",
            phoneLabel: "Phone",
            mobileLabel: "Mobile",
            emailLabel: "Email",
            websiteLabel: "Website",
            profileOverline: "Company Profile",
            downloadTitle: "Download the full Kharisma Damar Satya company profile",
            downloadCta: "Click here to download company detail",
        },
        footer: {
            navigate: "Navigate",
            companies: "Companies",
            contact: "Contact",
            tagline: "“Building the Future of Energy & Infrastructure”",
            vision: "Global Vision. Infinite Impact.",
            copyright: "© 2026 Sadja Core Corp. All Rights Reserved.",
            backToTop: "Back to top",
        },
        company: {
            backTo: "Back to Sadja Core",
            about: "About",
            companyProfile: "Company Profile",
            direction: "Direction",
            visionMission: "Vision & Mission",
            vision: "Vision",
            mission: "Mission",
            strengthsOverline: "What Sets Us Apart",
            strengthsTitle: "Our Strength",
            specializations: "Our Specializations",
            capabilities: "Capabilities",
            services: "Services",
            epccOverline: "End-to-End EPCC Solutions",
            epccTitle: "Engineering · Procurement · Construction · Commissioning",
            epccDesc:
                "PT. Orbito offers a full spectrum of EPCC services, ensuring seamless project delivery from conceptualization to operational handover.",
            projectFocusOverline: "Our Project Focus",
            projectFocusTitle: "Power Generation Platforms",
            specializationOverline: "Our Specialization",
            partnership: "Partnership",
            whyPartner: "Why Partner with PT. Orbito?",
            expertiseProjects: "Expertise & Projects",
            projectExperience: "Project Experience",
            selectedPortfolio: "Selected Project Portfolio",
            trackRecordOverline: "Rekam Jejak",
            trackRecord: "Track Record",
            workAreasOverline: "Area Kerja",
            workAreasTitle: "Project Portfolio by Region",
            partnersOverline: "Jejak Mitra",
            partners: "Partners",
            managementOverline: "Kabinet Kerja",
            management: "Management",
            compliance: "Compliance",
            legality: "Company Legality",
            contact: "Contact",
            readyTitle: "Ready to power your next project?",
            profileOverline: "Company Profile",
            downloadTitle: (name) => `Download the full ${name} company profile`,
            downloadCta: "Click here to download company detail",
        },
    },
    id: {
        navCta: "Jelajahi Grup Kami",
        hero: {
            overline: "Sadja Core Corp — Perusahaan Induk Investasi",
            lines: ["Membangun", "Masa Depan", "Energi & Infrastruktur"],
            sub: "Perusahaan induk investasi yang membangun platform berskala di bidang energi, transmisi listrik, dan infrastruktur strategis.",
            tagline: "Visi Global. Dampak Tanpa Batas.",
            primary: "Jelajahi Grup Kami",
            secondary: "Perusahaan Kami",
            strip: ["Energi", "Transmisi", "Infrastruktur"],
            scroll: "Gulir",
            imgAlt: "Menara transmisi listrik tegangan tinggi saat matahari terbenam",
        },
        about: {
            chapter: "CH.01",
            overline: "Tentang Sadja Core",
            title: "Perusahaan Induk Investasi untuk Platform Generasi Berikutnya",
            lead: "Sadja Core Corp adalah perusahaan induk investasi yang berfokus membangun platform berskala di sektor energi, transmisi listrik, dan infrastruktur strategis.",
            objectiveLabel: "Tujuan Strategis",
            objective:
                "Membangun platform infrastruktur bernilai miliaran dolar untuk melayani kebutuhan pembangkitan listrik, jaringan grid, utilitas industri, dan transisi energi global.",
            imgAlt: "Arsitektur gedung tinggi korporat",
        },
        companies: {
            chapter: "CH.02",
            overline: "Grup Kami",
            title: "Empat Perusahaan. Satu Platform.",
            description:
                "Sadja Core Corp beroperasi melalui empat anak perusahaan yang fokus — masing-masing platform spesialis, bersama-sama membentuk grup energi dan infrastruktur yang terintegrasi.",
            more: "kapabilitas lainnya",
            explore: "Jelajahi",
        },
        ecosystem: {
            chapter: "CH.03",
            overline: "Ekosistem Grup",
            title: "Satu Induk. Empat Mesin.",
            description:
                "Setiap anak perusahaan saling memperkuat — keahlian transmisi, kapabilitas pembangkitan listrik, dan penyampaian konstruksi berpadu dalam satu platform investasi.",
            centerSub: "INDUK INVESTASI",
            branches: [
                { name: "DIGON", sub: "Transmisi Listrik", tag: "GRID", route: "/companies/digon" },
                { name: "ORBITO", sub: "Energi Terintegrasi", tag: "DAYA", route: "/companies/orbito" },
                { name: "SADJA GROUP", sub: "Konstruksi, Properti & Stringing SUTET", tag: "INFRASTRUKTUR", route: "/companies/sadja-group" },
                { name: "KDS", sub: "Kontraktor Umum & Konstruksi", tag: "KONTRAKTOR UMUM", route: "/companies/kharisma-damar-satya" },
            ],
            caption:
                "Digon membangun grid. Orbito mengalirkannya. Sadja Group dan Kharisma Damar Satya membangun dunia di sekitarnya — platform yang saling melengkapi dalam satu struktur induk.",
        },
        platforms: {
            chapter: "CH.04",
            overline: "Platform Kami",
            title: "Kapabilitas Tulang Punggung Grup",
            description: "Platform eksekusi inti yang dibawa masing-masing perusahaan operasi ke dalam grup.",
            tabs: ["PT. Digon Power", "PT. Sadja Group Indonesia", "PT. Orbito Integrated Energy", "PT. Kharisma Damar Satya"],
        },
        why: { chapter: "CH.05", overline: "Mengapa Sadja Core?", title: "Dibangun untuk Siklus Jangka Panjang" },
        roadmap: {
            chapter: "CH.06",
            overline: "Peta Jalan Strategis",
            title: "2026 — 2030",
            description:
                "Dari fondasi operasional menuju grup infrastruktur regional — evolusi bertahap yang terencana dari kontraktor menjadi pemilik aset.",
            targetMarkets: "Pasar Target",
        },
        asset: {
            chapter: "CH.07",
            overline: "PT. Sadja Core Corp.",
            title: "Strategi Kepemilikan Aset",
            description:
                "Strategi institusional tiga tahap — dari eksekusi asset-light menuju platform kepemilikan aset infrastruktur multi-triliun Rupiah.",
            stage: "Tahap",
        },
        experience: {
            chapter: "CH.08",
            overline: "Proyek / Kapabilitas",
            title: "Kapabilitas & Pengalaman Grup",
            description:
                "Pengalaman gabungan puluhan tahun dalam jalur transmisi, gardu induk, konstruksi sipil, dan pembangkitan listrik — di seluruh Indonesia dan Timor-Leste.",
            years: "Tahun Pengalaman",
            viewProfile: "Lihat Profil Perusahaan",
            allGroup: "Semua Grup",
            openKds: "Buka Profil Lengkap Kharisma Damar Satya",
        },
        investors: {
            chapter: "CH.09",
            overline: "Hubungan Investor",
            titleA: "Membangun Nilai",
            titleB: "Infrastruktur Jangka Panjang",
            body: "Sadja Core Corp membuka kemitraan dengan institusi global untuk mentransformasi infrastruktur pasar berkembang.",
            cta: "Diskusikan Kemitraan",
        },
        leadership: {
            chapter: "CH.10",
            overline: "Kepemimpinan",
            title: "Tim Manajemen",
            description:
                "Kepemimpinan berpengalaman di bidang strategi korporasi, tata kelola, dan manajemen permodalan.",
            openPosition: "Posisi Terbuka",
        },
        contact: {
            chapter: "CH.11",
            overline: "Kontak",
            title: "Mulai Percakapan",
            description:
                "Untuk kemitraan, proyek, dan pertanyaan investor — hubungi tim Sadja Core Corp secara langsung.",
            emailLabel: "Email",
            officeLabel: "Kantor Pusat",
            name: "Nama",
            email: "Email",
            subject: "Subjek",
            message: "Pesan",
            namePh: "Nama lengkap Anda",
            emailPh: "anda@perusahaan.com",
            subjectPh: "Pertanyaan kemitraan / proyek",
            messagePh: "Ceritakan proyek atau minat kemitraan Anda…",
            send: "Kirim Pesan",
            toast: "Membuka aplikasi email Anda — pesan siap dikirim.",
            enquiryFrom: "Pertanyaan dari",
            website: "Situs Web",
        },
        kds: {
            seoTitle: "PT. Kharisma Damar Satya | Sadja Core Corp",
            seoDesc: "PT. Kharisma Damar Satya is an experienced Indonesian construction company with project experience across industrial, infrastructure, hospitality, residential, office and commercial sectors.",
            category: "KONTRAKTOR UMUM & KONSTRUKSI",
            headline1: "Building with Experience.",
            headline2: "Delivering with Trust.",
            heroSub: "PT. Kharisma Damar Satya adalah salah satu perusahaan konstruksi swasta di Indonesia yang telah berpengalaman.",
            heroSupport: "Kami telah menyelesaikan proyek-proyek di sektor residensial, perkantoran, komersial, pabrik, gudang, dan infrastruktur — berkantor pusat di Jakarta.",
            backTo: "Kembali ke Sadja Core",
            exploreProjects: "Jelajahi Proyek Kami",
            aboutOverline: "PT. Kharisma Damar Satya",
            aboutTitle: "Tentang Kharisma Damar Satya",
            originalLabel: "Asli — Bahasa Indonesia",
            translationLabel: "English Translation",
            vmTitle: "Visi & Misi",
            vision: "Visi",
            mission: "Misi",
            sectorsOverline: "Bidang Kami",
            sectorsTitle: "Sektor Bisnis Kami",
            legalityOverline: "Kepatuhan",
            legalityTitle: "Legalitas Perusahaan",
            classOverline: "Lingkup Terdaftar",
            classTitle: "Klasifikasi Usaha",
            classSearchPh: "Cari kode atau deskripsi…",
            certOverline: "Mutu & Keselamatan",
            certTitle: "Sertifikasi & Sistem Manajemen",
            portfolioOverline: "Rekam Jejak",
            portfolioTitle: "Portofolio Proyek",
            filterSector: "Sektor",
            filterStatus: "Status",
            filterAll: "Semua",
            fields: { client: "Klien", project: "Proyek", location: "Lokasi", scope: "Lingkup", sector: "Sektor", status: "Status", year: "Tahun" },
            backToPortfolio: "Kembali ke Portofolio Proyek",
            expOverline: "Kapabilitas",
            expTitle: "Pengalaman Kami",
            contactOverline: "Hubungi Kami",
            contactTitle: "Kontak",
            addressLabel: "Kantor",
            phoneLabel: "Telepon",
            mobileLabel: "Seluler",
            emailLabel: "Email",
            websiteLabel: "Situs Web",
            profileOverline: "Profil Perusahaan",
            downloadTitle: "Unduh profil perusahaan Kharisma Damar Satya lengkap",
            downloadCta: "Klik di sini untuk mengunduh detail perusahaan",
        },
        footer: {
            navigate: "Navigasi",
            companies: "Perusahaan",
            contact: "Kontak",
            tagline: "“Membangun Masa Depan Energi & Infrastruktur”",
            vision: "Visi Global. Dampak Tanpa Batas.",
            copyright: "© 2026 Sadja Core Corp. Seluruh Hak Cipta Dilindungi.",
            backToTop: "Kembali ke atas",
        },
        company: {
            backTo: "Kembali ke Sadja Core",
            about: "Tentang",
            companyProfile: "Profil Perusahaan",
            direction: "Arah",
            visionMission: "Visi & Misi",
            vision: "Visi",
            mission: "Misi",
            strengthsOverline: "Yang Membedakan Kami",
            strengthsTitle: "Keunggulan Kami",
            specializations: "Spesialisasi Kami",
            capabilities: "Kapabilitas",
            services: "Layanan",
            epccOverline: "Solusi EPCC Menyeluruh",
            epccTitle: "Engineering · Procurement · Construction · Commissioning",
            epccDesc:
                "PT. Orbito menawarkan spektrum layanan EPCC yang lengkap, memastikan penyampaian proyek yang mulus dari konseptualisasi hingga serah terima operasional.",
            projectFocusOverline: "Fokus Proyek Kami",
            projectFocusTitle: "Platform Pembangkit Listrik",
            specializationOverline: "Spesialisasi Kami",
            partnership: "Kemitraan",
            whyPartner: "Mengapa Bermitra dengan PT. Orbito?",
            expertiseProjects: "Keahlian & Proyek",
            projectExperience: "Pengalaman Proyek",
            selectedPortfolio: "Portofolio Proyek Terpilih",
            trackRecordOverline: "Track Record",
            trackRecord: "Rekam Jejak",
            workAreasOverline: "Area Kerja",
            workAreasTitle: "Portofolio Proyek per Wilayah",
            partnersOverline: "Jejak Mitra",
            partners: "Mitra",
            managementOverline: "Kabinet Kerja",
            management: "Manajemen",
            compliance: "Kepatuhan",
            legality: "Legalitas Perusahaan",
            contact: "Kontak",
            readyTitle: "Siap mengaliri proyek Anda berikutnya?",
            profileOverline: "Profil Perusahaan",
            downloadTitle: (name) => `Unduh profil perusahaan ${name} lengkap`,
            downloadCta: "Klik di sini untuk mengunduh detail perusahaan",
        },
    },
};

export const useContent = () => {
    const { lang, setLang } = useLang();
    const companies = COMPANIES_BY_LANG[lang] || COMPANIES_BY_LANG.en;
    return {
        lang,
        setLang,
        ui: UI[lang] || UI.en,
        site: SITE[lang] || SITE.en,
        companies,
        getCompany: (slug) => companies.find((c) => c.slug === slug),
    };
};
